import React, { useEffect, useState, useMemo, useCallback } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import { Button, Badge, Card, Form, Row, Col } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaToggleOff, FaToggleOn } from "react-icons/fa";

import {
  getAllCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  getAdminLookups,
} from "../../services/adminApi";

import ReusableAdminTable from "./ReusableAdminTable";

import "./CouponRedeem.css";

const CouponRedeem = () => {
  // =========================================
  // STATES
  // =========================================

  const [coupons, setCoupons] = useState([]);
  const [lookupGroups, setLookupGroups] = useState([]);

  const [showForm, setShowForm] = useState(false);

  const [editId, setEditId] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    coupon_code: "",
    coupon_type: "fix",
    amount: "",
    start_date: "",
    end_date: "",
    status: true,

    project_type: [],
    role_category: [],
    audition_type: "",

    no_of_coupons_available: "",

    can_use_multiple_time: false,
  });

  // =========================================
  // LOAD LOOKUPS
  // =========================================

  const loadLookups = useCallback(async () => {
    try {
      const res = await getAdminLookups();

      const filtered = res?.data?.filter((lk) =>
        ["projectType", "jobRoleCategory", "auditionType"].includes(
          lk.lookupGroup
        )
      );

      setLookupGroups(filtered || []);
    } catch (err) {
      console.log(err);
    }
  }, []);

  // =========================================
  // LOAD COUPONS
  // =========================================

  const loadCoupons = useCallback(async () => {
    setIsLoading(true);

    try {
      const res = await getAllCoupons();

      if (res?.data?.success) {
        setCoupons(res.data.data || []);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load coupons",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =========================================
  // INITIAL LOAD
  // =========================================

  useEffect(() => {
    loadCoupons();
    loadLookups();
  }, [loadCoupons, loadLookups]);

  // =========================================
  // LOOKUP DATA
  // =========================================

  const projectTypes =
    lookupGroups.find((g) => g.lookupGroup === "projectType")
      ?.lookupValue?.data || [];

  const roleCategories =
    lookupGroups.find((g) => g.lookupGroup === "jobRoleCategory")
      ?.lookupValue?.data || [];

  const auditionTypes =
    lookupGroups.find((g) => g.lookupGroup === "auditionType")
      ?.lookupValue?.data || [];

  // =========================================
  // RESET FORM
  // =========================================

  const resetForm = () => {
    setEditId(null);

    setShowForm(false);

    setFormData({
      coupon_code: "",
      coupon_type: "fix",
      amount: "",
      start_date: "",
      end_date: "",
      status: true,

      project_type: [],
      role_category: [],
      audition_type: "",

      no_of_coupons_available: "",

      can_use_multiple_time: false,
    });
  };

  // =========================================
  // SUBMIT
  // =========================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,

        project_type: formData.project_type.join(","),

        role_category: formData.role_category.join(","),

        audition_type: formData.audition_type,
      };

      if (editId) {
        const res = await updateCoupon(editId, payload);

        Swal.fire({
          icon: "success",
          title: "Success",
          text:
            res?.data?.message || "Coupon Updated Successfully",
          confirmButtonColor: "#2563eb",
        });
      } else {
        const res = await createCoupon(payload);

        Swal.fire({
          icon: "success",
          title: "Success",
          text:
            res?.data?.message || "Coupon Created Successfully",
          confirmButtonColor: "#2563eb",
        });
      }

      resetForm();

      loadCoupons();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          "Something went wrong",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  // =========================================
  // EDIT
  // =========================================

  const handleEdit = (coupon) => {
    setEditId(coupon.id);

    setFormData({
      coupon_code: coupon.coupon_code || "",

      coupon_type: coupon.coupon_type || "fix",

      amount: coupon.amount || "",

      start_date: coupon.start_date || "",

      end_date: coupon.end_date || "",

      status: coupon.status,

      project_type: coupon.project_type
        ? coupon.project_type.split(",")
        : [],

      role_category: coupon.role_category
        ? coupon.role_category.split(",")
        : [],

      audition_type: coupon.audition_type || "",

      no_of_coupons_available:
        coupon.no_of_coupons_available || "",

      can_use_multiple_time:
        coupon.can_use_multiple_time || false,
    });

    setShowForm(true);
  };

  // =========================================
  // DELETE
  // =========================================

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this coupon?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await deleteCoupon(id);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text:
          res?.data?.message ||
          "Coupon Deleted Successfully",
        confirmButtonColor: "#2563eb",
      });

      loadCoupons();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text:
          err?.response?.data?.message ||
          "Something went wrong",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  // =========================================
  // TABLE COLUMNS
  // =========================================

  const columns = useMemo(
    () => [
      {
        accessorKey: "coupon_code",
        header: "Code",
      },

      {
        accessorKey: "coupon_type",
        header: "Type",

        cell: (info) => (
          <Badge bg="info">
            {info.getValue() === "fix"
              ? "Fixed"
              : "Percentage"}
          </Badge>
        ),
      },

      {
        accessorKey: "amount",
        header: "Amount",

        cell: (info) => {
          const row = info.row.original;

          return row.coupon_type === "fix"
            ? `₹ ${row.amount}`
            : `${row.amount}%`;
        },
      },

      {
        accessorKey: "start_date",
        header: "Start Date",

        cell: (info) => info.getValue() || "-",
      },

      {
        accessorKey: "end_date",
        header: "End Date",

        cell: (info) => info.getValue() || "-",
      },

      {
        accessorKey: "status",
        header: "Status",

        cell: (info) =>
          info.getValue() ? (
            <span className="active-status">
              <FaToggleOn className="me-1" />
              Active
            </span>
          ) : (
            <span className="inactive-status">
              <FaToggleOff className="me-1" />
              Inactive
            </span>
          ),
      },

      {
        accessorKey: "project_type",
        header: "Project",

        cell: (info) => info.getValue() || "-",
      },

      {
        accessorKey: "role_category",
        header: "Role Category",

        cell: (info) => info.getValue() || "-",
      },

      {
        accessorKey: "audition_type",
        header: "Audition Type",

        cell: (info) => info.getValue() || "-",
      },

      {
        accessorKey: "no_of_coupons_available",
        header: "Available",
      },

      {
        accessorKey: "can_use_multiple_time",
        header: "Usage",

        cell: (info) =>
          info.getValue() ? (
            <Badge bg="success">Multi Use</Badge>
          ) : (
            <Badge bg="secondary">One Time</Badge>
          ),
      },

      {
        id: "actions",
        header: "Actions",

        cell: (info) => (
          <div className="d-flex gap-2 justify-content-center">
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                handleEdit(info.row.original)
              }
            >
              <FaEdit />
            </Button>

            <Button
              size="sm"
              variant="danger"
              onClick={() =>
                handleDelete(info.row.original.id)
              }
            >
              <FaTrash />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  // =========================================
  // RENDER
  // =========================================

  return (
    <div className="coupon-page">
      {/* ========================================= */}
      {/* FORM */}
      {/* ========================================= */}

      {showForm ? (
        <Card className="coupon-card border-0">
          <Card.Body>
            <div className="coupon-header">
              <h2>
                {editId ? "Edit Coupon" : "Add Coupon"}
              </h2>

              <Button
                variant="secondary"
                onClick={resetForm}
              >
                Cancel
              </Button>
            </div>

            <Form onSubmit={handleSubmit}>
              <Row>
                {/* CODE */}
                <Col md={12} className="mb-4">
                  <Form.Label>
                    Coupon Code
                  </Form.Label>

                  <Form.Control
                    type="text"
                    className="coupon-input"
                    value={formData.coupon_code}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        coupon_code: e.target.value,
                      })
                    }
                    required
                  />
                </Col>

                {/* TYPE */}
                <Col md={6} className="mb-4">
                  <Form.Label>
                    Coupon Type
                  </Form.Label>

                  <Form.Select
                    className="coupon-input"
                    value={formData.coupon_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        coupon_type: e.target.value,
                      })
                    }
                  >
                    <option value="fix">
                      Fixed
                    </option>

                    <option value="per">
                      Percentage
                    </option>
                  </Form.Select>
                </Col>

                {/* AMOUNT */}
                <Col md={6} className="mb-4">
                  <Form.Label>
                    Amount
                  </Form.Label>

                  <Form.Control
                    type="number"
                    className="coupon-input"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        amount: e.target.value,
                      })
                    }
                    required
                  />
                </Col>

                {/* START DATE */}
                <Col md={6} className="mb-4">
                  <Form.Label>
                    Start Date
                  </Form.Label>

                  <Form.Control
                    type="date"
                    className="coupon-input"
                    value={formData.start_date}
                    min={
                      new Date()
                        .toISOString()
                        .split("T")[0]
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        start_date: e.target.value,
                      })
                    }
                  />
                </Col>

                {/* END DATE */}
                <Col md={6} className="mb-4">
                  <Form.Label>
                    End Date
                  </Form.Label>

                  <Form.Control
                    type="date"
                    className="coupon-input"
                    value={formData.end_date}
                    min={formData.start_date}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        end_date: e.target.value,
                      })
                    }
                  />
                </Col>

                {/* PROJECT TYPE */}
                <Col md={4} className="mb-4">
                  <Form.Label>
                    Project Type
                  </Form.Label>

                  <Select
                    isMulti
                    className="coupon-multi-select"
                    classNamePrefix="select"
                    options={[
                      {
                        value: "all",
                        label: "All",
                      },

                      ...projectTypes.map((item) => ({
                        value: item.lookupLabel,
                        label: item.lookupLabel,
                      })),
                    ]}
                    value={projectTypes
                      .filter((item) =>
                        formData.project_type.includes(
                          item.lookupLabel
                        )
                      )
                      .map((item) => ({
                        value: item.lookupLabel,
                        label: item.lookupLabel,
                      }))}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,

                        project_type: selected
                          ? selected.map((x) => x.value)
                          : [],
                      })
                    }
                  />
                </Col>

                {/* ROLE CATEGORY */}
                <Col md={4} className="mb-4">
                  <Form.Label>
                    Role Category
                  </Form.Label>

                  <Select
                    isMulti
                    className="coupon-multi-select"
                    classNamePrefix="select"
                    options={[
                      {
                        value: "all",
                        label: "All",
                      },

                      ...roleCategories.map((item) => ({
                        value: item.lookupLabel,
                        label: item.lookupLabel,
                      })),
                    ]}
                    value={roleCategories
                      .filter((item) =>
                        formData.role_category.includes(
                          item.lookupLabel
                        )
                      )
                      .map((item) => ({
                        value: item.lookupLabel,
                        label: item.lookupLabel,
                      }))}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,

                        role_category: selected
                          ? selected.map((x) => x.value)
                          : [],
                      })
                    }
                  />
                </Col>

                {/* AUDITION TYPE */}
                <Col md={4} className="mb-4">
                  <Form.Label>
                    Audition Type
                  </Form.Label>

                  <Form.Select
                    className="coupon-input"
                    value={formData.audition_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        audition_type: e.target.value,
                      })
                    }
                  >
                    <option value="">
                      Select Audition Type
                    </option>

                    {auditionTypes.map((item, index) => (
                      <option
                        key={index}
                        value={item.lookupLabel}
                      >
                        {item.lookupLabel}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                {/* STATUS */}
                <Col md={6} className="mb-4">
                  <Form.Label>
                    Coupon Status
                  </Form.Label>

                  <Form.Select
                    className="coupon-input"
                    value={
                      formData.status
                        ? "true"
                        : "false"
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status:
                          e.target.value === "true",
                      })
                    }
                  >
                    <option value="true">
                      Active
                    </option>

                    <option value="false">
                      Inactive
                    </option>
                  </Form.Select>
                </Col>

                {/* AVAILABLE */}
                <Col md={6} className="mb-4">
                  <Form.Label>
                    Available Coupons
                  </Form.Label>

                  <Form.Control
                    type="number"
                    className="coupon-input"
                    value={
                      formData.no_of_coupons_available
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        no_of_coupons_available:
                          e.target.value,
                      })
                    }
                    required
                  />
                </Col>

                {/* MULTI USE */}
                <Col
                  md={6}
                  className="d-flex align-items-center mt-3"
                >
                  <Form.Check
                    type="checkbox"
                    checked={
                      formData.can_use_multiple_time
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        can_use_multiple_time:
                          e.target.checked,
                      })
                    }
                    label="Allow Multiple Use"
                  />
                </Col>

                {/* BUTTON */}
                <Col md={12} className="mt-4">
                  <Button type="submit">
                    {editId
                      ? "Update Coupon"
                      : "Save Coupon"}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      ) : (
        <ReusableAdminTable
          data={coupons}
          columns={columns}
          isLoading={isLoading}
          title="Coupon Management"
          onAddNew={() => {
            resetForm();
            setShowForm(true);
          }}
          addNewButtonText="Add Coupon"
          searchPlaceholder="Search coupons..."
          exportCsvFilename="coupon-list.csv"
        />
      )}
    </div>
  );
};

export default CouponRedeem;